import { Box, Button, Divider, Fade, Typography } from '@mui/material';
import { SxProps } from '@mui/system';
import { FunctionComponent } from 'react';

import BrowseColour from '/public/icons/layout/browse-colour.svg';
import BrowseGrayscale from '/public/icons/layout/browse-grayscale.svg';
import FavouritesColour from '/public/icons/layout/favourites-colour.svg';
import FavouritesGrayscale from '/public/icons/layout/favourites-grayscale.svg';
import PraharColour from '/public/icons/layout/prahar-colour.svg';
import PraharGrayscale from '/public/icons/layout/prahar-grayscale.svg';
import Link from 'next/link';
import { useSelector } from 'react-redux';
import {
  playerModeSelector,
  playingStateSelector,
} from '../../redux/slices/playerSlice';
import { PlayingMode } from '../../redux/types.generated';
import PraharSelected from '/public/icons/nav-icons/prahar-selected.svg';
import PraharUnselected from '/public/icons/nav-icons/prahar-unselected.svg';
import PraharUnselectedPlaying from '/public/icons/navbar-icons/prahar-playing.svg';
import PraharSelectedPlaying from '/public/icons/navbar-icons/prahar-selected-playing.svg';
import BrowseSelected from '/public/icons/nav-icons/browse-selected.svg';
import BrowseUnselected from '/public/icons/nav-icons/browse-unselected.svg';
import BrowseSelectedPlaying from '/public/icons/navbar-icons/browse-selected-playing.svg';
import BrowseUnselectedPlaying from '/public/icons/navbar-icons/browse-unselected-playing.svg';
import FavSelected from '/public/icons/nav-icons/fav-selected.svg';
import FavUnselected from '/public/icons/nav-icons/fav-unselected.svg';
import FavSelectedPlaying from '/public/icons/navbar-icons/fav-selected-playing.svg';
import FavUnselectedPlaying from '/public/icons/navbar-icons/fav-unselected-playing.svg';
import { PlayingStateEnum } from '../../enums/PlayerEnums';

import Image from 'next/image';
import PraharSelectedPlayingGif from '/public/icons/nav-icons/prahar-selected-playing.png';
import PraharUnselectedPlayingGif from '/public/icons/nav-icons/prahar-unselected-playing.png';
import BrowseSelectedPlayingGif from '/public/icons/nav-icons/browse-selected-playing.png';
import BrowseUnselectedPlayingGif from '/public/icons/nav-icons/browse-unselected-playing.png';
import FavSelectedPlayingGif from '/public/icons/nav-icons/fav-selected-playing.png';
import FavUnselectedPlayingGif from '/public/icons/nav-icons/fav-unselected-playing.png';

const browseRegex = /(\/ragas)|(\/instruments)|(\/artistes)|(\/browse)/;

interface NavTabProps {
  active: boolean;
  href?: string;
  label: React.ReactNode; // changed from string
  sx?: SxProps;
}

// TODO change the playlist pages to use MUI tabs instead of this component

/**
 * Generic Navigation Tab component
 * @param active The condition determining whether tab is active
 * @param changeTab The callback to set tab state
 * @param label The tab label
 *
 */
const NavTab: FunctionComponent<NavTabProps> = ({
  active,
  href = '/',
  label,
  sx,
}) => {
  const playingState = useSelector(playingStateSelector);
  const playerMode = useSelector(playerModeSelector);

  const getCurrentPlayingTab = () => {
    if (playingState !== PlayingStateEnum.Play) return '';
    return playerMode;
  };

  const currentPlayingTab = getCurrentPlayingTab();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        boxSizing: 'border-box',
      }}
    >
      <Link href={href} passHref>
        <Box>
          <Button
            disableRipple
            sx={{
              padding: 1,
              minWidth: 'auto',
              minHeight: '54px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexGrow: 1,
              backdropFilter: 'blur(4px)',
              '&:hover': {
                boxShadow: '0px',
                backgroundColor: 'transparent',
              },
              '& .MuiButton-startIcon': {
                mr: 0,
                ml: 0,
              },
              textTransform: 'none',
              pb: 1.5,
              ...sx,
            }}
            startIcon={
              href === '/prahar' ? (
                currentPlayingTab === PlayingMode.Prahar ? (
                  active ? (
                    // <PraharSelectedPlaying />
                    <Image
                      src={PraharSelectedPlayingGif}
                      alt='PraharSelectedPlayingGif'
                      width={37}
                      height={36}
                    />
                  ) : (
                    // <PraharUnselectedPlaying />
                    <Image
                      src={PraharUnselectedPlayingGif}
                      alt='PraharUnselectedPlayingGif'
                      width={37}
                      height={36}
                    />
                  )
                ) : active ? (
                  <PraharSelected />
                ) : (
                  <PraharUnselected />
                )
              ) : browseRegex.test(href) ? (
                currentPlayingTab === PlayingMode.Playlist ? (
                  active ? (
                    // <BrowseSelectedPlaying />
                    <Image
                      src={BrowseSelectedPlayingGif}
                      alt='BrowseSelectedPlayingGif'
                      width={37}
                      height={36}
                    />
                  ) : (
                    // <BrowseUnselectedPlaying />
                    <Image
                      src={BrowseUnselectedPlayingGif}
                      alt='BrowseUnselectedPlayingGif'
                      width={37}
                      height={36}
                    />
                  )
                ) : active ? (
                  <BrowseSelected />
                ) : (
                  <BrowseUnselected />
                )
              ) : href === '/favourites' ? (
                currentPlayingTab === PlayingMode.Favourite ? (
                  active ? (
                    // <FavSelectedPlaying />
                    <Image
                      src={FavSelectedPlayingGif}
                      alt='FavSelectedPlayingGif'
                      width={37}
                      height={36}
                    />
                  ) : (
                    // <FavUnselectedPlaying />
                    <Image
                      src={FavUnselectedPlayingGif}
                      alt='FavUnselectedPlayingGif'
                      width={37}
                      height={36}
                    />
                  )
                ) : active ? (
                  <FavSelected />
                ) : (
                  <FavUnselected />
                )
              ) : (
                ''
              )
            }
          ></Button>
        </Box>
      </Link>
    </Box>
  );
};

export default NavTab;
